import isNumber from 'lodash/isNumber'

import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import { InvoiceFormDescriptionLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormDescriptionLine'
import { InvoiceFormLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormLine'
import { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { InvoiceFormSubtotalLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormSubtotalLine'
import { InvoicePayloadLine } from '@features/invoices/types/invoicePayload/invoicePayloadLine'
import { InvoiceLine } from '@features/invoices/types/invoices/invoiceLine'
import { calculateRowLineTotals } from '@features/invoices/utils/calculateRowLineTotals'
import { sumRowLineTotals } from '@features/invoices/utils/sumRowLineTotals'
import type { VatRate } from '@features/vatRates/types/vatRate'
import { findVatRateByPercent } from '@features/vatRates/utils/findVatRateByPercent'

interface GetLinesValuesOptions {
  currency: string
  defaultLineValues: InvoiceFormLine
  discountType: InvoiceDiscountType
  isCreditNote?: boolean
  isDiscountEnabled?: boolean
  lines: InvoicePayloadLine[] | InvoiceLine[]
  vatRates?: VatRate[]
}

export const getLinesValues = (options: GetLinesValuesOptions): InvoiceFormLine[] => {
  const {
    currency,
    defaultLineValues,
    discountType,
    isCreditNote,
    isDiscountEnabled = true,
    lines,
    vatRates = [],
  } = options
  const result: InvoiceFormLine[] = []
  let rowLinesSinceSubtotal: InvoiceFormRowLine[] = []

  for (const line of lines ?? []) {
    if (line.type === InvoiceLineType.Row) {
      const {
        date,
        description,
        discount: lineDiscount,
        productType,
        quantity,
        unit,
        unitPrice,
        vatRateId: lineVatRateId,
        vatPct,
      } = line

      // vatRateId and vatRatePercent must come from the same source - falling back on them
      // independently can pair one line's rate id with another line's percentage.
      const hasLineVatRate = isNumber(vatPct) && !!lineVatRateId
      // Some posted documents store vatPct without vatRateId - match the rate by its value
      // instead of replacing it with the organization's default rate.
      const vatRateByPercent = hasLineVatRate ? undefined : findVatRateByPercent(vatRates, vatPct)
      const defaultVatRateId = 'vatRateId' in defaultLineValues ? defaultLineValues?.vatRateId : undefined
      const defaultVatRatePercent = 'vatRatePercent' in defaultLineValues ? defaultLineValues?.vatRatePercent : 0
      const vatRateId = hasLineVatRate ? lineVatRateId : (vatRateByPercent?.id ?? defaultVatRateId)
      const vatRatePercent = hasLineVatRate ? vatPct : (vatRateByPercent?.rate ?? defaultVatRatePercent)
      const discount = isDiscountEnabled ? (lineDiscount ?? 0) : 0
      const resolvedUnitPrice = Number.isFinite(unitPrice) ? unitPrice : 0
      const resolvedQuantity = Number.isFinite(quantity) ? quantity : 0

      // Re-derived from unitPrice rather than copied from the stored netTotal/grossTotal: the summary is
      // always computed from unitPrice by the package, so a stale half-cent total (3.65 × 10% stored
      // as 4.01) would no longer match it and the backend rejects approve with `invalid total`.
      const totals = calculateRowLineTotals({
        currency,
        discountType,
        isCreditNote,
        line: { discount, quantity: resolvedQuantity, unitPrice: resolvedUnitPrice, vatRatePercent },
      })

      const rowLine: InvoiceFormRowLine = {
        ...defaultLineValues,
        date: new Date(date),
        discount,
        productName: description,
        productType,
        quantity: resolvedQuantity,
        ...totals,
        type: InvoiceLineType.Row,
        unit,
        unitPrice: resolvedUnitPrice,
        vatRateId,
        vatRatePercent,
      }

      rowLinesSinceSubtotal.push(rowLine)
      result.push(rowLine)
    } else if (line.type === InvoiceLineType.Description) {
      const descriptionLine: InvoiceFormDescriptionLine = {
        content: line.content,
        type: InvoiceLineType.Description,
      }

      result.push(descriptionLine)
    } else if (line.type === InvoiceLineType.Subtotal) {
      const subtotalLine: InvoiceFormSubtotalLine = {
        ...sumRowLineTotals(rowLinesSinceSubtotal),
        type: InvoiceLineType.Subtotal,
      }

      rowLinesSinceSubtotal = []
      result.push(subtotalLine)
    }
  }

  return result
}
